
import isEmpty from 'lodash/isEmpty';
import isUndefined from 'lodash/isUndefined';

import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import { IActivityDetail } from '@common-types';
import { BookingErrorsEnum, ISessionKey, ITrustYouReview } from '@share/common-types';
import { SESSION_SEARCH_EXPIRED_MESSAGE, SESSION_EXPIRED_STATUS, Urls, Routes, SESSION_KEY_LABEL_ROOMS, SESSION_KEY_LABEL } from '@share/constants';
import { SERVER_ERROR_STATUSES } from '@share/constants';
import { getHeaders, axiosInstance, UrlUtils, getSessionObj, delay, getSelectedCurrency, getTimeout, getActivityOccupancies, RootState } from '@share/utils';
import { datesActions, activityActions, activityLocationActions, trustYouReviewActions, activityAvailabilityActions, activitySavedPropertyActions } from '@share/store/slices';
import { NULL_VALUE } from '@constants';


export interface IActivityDetailsState {
  activity: IActivityDetail | null | undefined;
  loading: boolean;
  loadingRoom: boolean;
  isServerError: boolean;
  error: string;
}

const initialState: IActivityDetailsState = {
  activity: null,
  loading: false,
  loadingRoom: true,
  error: '',
  isServerError: false
};

const zero = 0;

const activityDetailsSlice = createSlice({
  name: 'activity-details',
  initialState,
  reducers: {
    setLoading: (state: IActivityDetailsState, { payload }: PayloadAction<boolean>) => {
      state.loading = payload;
    },
    setLoadingRoom: (state: IActivityDetailsState, { payload }: PayloadAction<boolean>) => {
      state.loadingRoom = payload;
    },
    setError: (state: IActivityDetailsState, { payload }: PayloadAction<string>) => {
      state.error = payload;
    },
    setActivityDetails: (state: IActivityDetailsState, { payload }: PayloadAction<any>) => {
      state.activity = {
        ...payload?.activities[0]
      };
    },
    setIsServerError: (state: IActivityDetailsState, { payload }: PayloadAction<boolean>) => {
      state.isServerError = payload;
    },
  },
});

export const activityDetailsActions = activityDetailsSlice.actions;


export const activityDetailsReducer = activityDetailsSlice.reducer;

const getActivity = async (
  ActivityCode: string,
  sessionKey: ISessionKey | null | undefined,
  quote: string | undefined,
  getState: any
) => {
  const { datesStore, roomsStore, loginStore } = getState();

  const startDate = datesStore?.startDate ?? null;
  const endDate = datesStore?.endDate ?? null;
  const roomselected = roomsStore?.roomsSelected ?? [];

  const payload: any = {
    ActivityCode,
    Residency: "US",
    From: startDate,
    To: endDate ?? startDate,
    Occupancies: getActivityOccupancies(roomselected) ?? [],
    Currency: getSelectedCurrency(loginStore?.account) ?? "USD",
    Language: "en",
    LifeStyle: loginStore?.lifeStyle ?? "None",
    sessionKey: sessionKey ?? null,
  };

  if (sessionKey) {
    payload.sessionKey = sessionKey;
  }

  if (quote) {
    payload.quote = quote;
  }

  return axiosInstance.post(
    Urls.ActivityDetails,
    payload,
    {
      ...getHeaders(),
    }
  );
};

const getActivityStatic = async (
  ActivityCode: string,
) => {
  return axiosInstance.get(`${Urls.ActivityDetailsStatic}${ActivityCode}`, { ...getHeaders() });
};

const getActivityRetry = async (ActivityCode: string, sessionKey: ISessionKey | undefined | null, quote: string | undefined, getState: any
) => {
  try {

    return await getActivity(ActivityCode, sessionKey,quote, getState);
  } catch (error) {
    delay(300);
    return await getActivity(ActivityCode, sessionKey, quote, getState);
  }
}

export const getActivityDetails = createAsyncThunk<any, { ActivityCode: string; sessionKey: ISessionKey | null | undefined; isStatic: boolean; quote?: string },
  { state: RootState }
>(
  'activity-details/getActivityDetails',
  async ({ ActivityCode, sessionKey, isStatic, quote }, { dispatch, getState }) => {

    dispatch(activityDetailsActions.setLoading(true));

    dispatch(activityDetailsActions.setError(''));
    dispatch(activityDetailsActions.setIsServerError(false));

    dispatch(datesActions.resetSelectedDates());
    dispatch(activityLocationActions.resetSelectedLocation());
    dispatch(activityAvailabilityActions.resetAvailability());

    const { loginStore, activityStore } = getState();
    const { account } = loginStore;
    try {
      let res;
      if(isStatic){
        const result = await getActivityStatic(ActivityCode);
        res = {
          data: {
            activities: [result.data]
          }
        }
      }else{
        res = await getActivityRetry(ActivityCode, sessionKey as ISessionKey, quote,  getState);
      }
      // A date with no availability is not the same as an activity that doesn't exist.
      // If the details response comes back empty for an already-loaded activity, keep the
      // current detail context on screen instead of wiping it — wiping it renders the
      // generic "Oops, could not find the specified Activity" empty page. The inline
      // no-availability message is surfaced separately by the checkAvailability flow.
      const fetchedActivity = res.data?.activities?.[0];
      const hasActivity = !isEmpty(fetchedActivity);

      if (hasActivity) {
        dispatch(activityDetailsActions.setActivityDetails(res.data));
        dispatch(activitySavedPropertyActions.syncSavedIds(res.data.activities));
      }

      dispatch(trustYouReviewActions.setReviewModule(res.data?.trustYouData?.metaReview?.response as ITrustYouReview));

      if (!res.data?.sessionKey?.isInvalid || activityStore.sessionKey == null) {
        const values = UrlUtils.getValues();
        const session = getSessionObj(
          values[SESSION_KEY_LABEL_ROOMS] as ISessionKey,
          values[SESSION_KEY_LABEL] as ISessionKey
        );

        const isSessionExpired = getTimeout(session?.expireDate) <= 0;

        if (isUndefined(session) || (isSessionExpired)) {
          dispatch(activityActions.setSessionKey(res.data.sessionKey));
          UrlUtils.setUrl(SESSION_KEY_LABEL, res.data.sessionKey);
        }
      }

      dispatch(activityDetailsActions.setLoading(false));
      
      return res.data;

    } catch (error: any) {

      dispatch(activityDetailsActions.setLoading(false));

      dispatch(
        activityDetailsActions.setError(
          !isEmpty(error?.code)
            ? error.code.toString()
            : 'GENERIC'
        )
      );

      if (
        error?.response?.status === SESSION_EXPIRED_STATUS &&
        error?.response?.data?.[zero] === SESSION_SEARCH_EXPIRED_MESSAGE
      ) {
        history.replaceState(
          null,
          NULL_VALUE,
          `/${account?.name}${Routes.ActivitiesSearch}${location.search}`
        );
        location.reload();
      } else if (SERVER_ERROR_STATUSES.includes(error?.response?.status)) {
        dispatch(activityDetailsActions.setIsServerError(true));
      } else if (error?.response?.data?.code === BookingErrorsEnum.RoomsUnavailable) {
        dispatch(activityDetailsActions.setError(BookingErrorsEnum.RoomsUnavailable));
      }
    }
  }
);






